import { on } from 'events';
import { stat } from 'fs';
import {
  MEVETH_CONTRACT,
  MEVETH_ROUTER_CONTRACT,
  NEXT_PUBLIC_URL,
  SPLIT_SWAP_ROUTER_CONTRACT,
  WETH_CONTRACT,
} from '@/constants/addresses';
import { useDebounce } from '@/hooks/useDebounce';
import { supabase } from '@/lib/supabaseClient';
import { UserData } from '@/pages/api/invites/getCurrentProfile';
import { device } from '@/styles/common';
import { captureCheckIn, captureException } from '@sentry/nextjs';
import { AnimatePresence, motion } from 'framer-motion';
import { atom, useAtom } from 'jotai';
import { signIn, signOut, useSession } from 'next-auth/react';
import { log } from 'next-axiom';
import { redirect } from 'next/dist/server/api-utils';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { use, useEffect, useState } from 'react';
import styled from 'styled-components';
import { formatEther, parseEther, recoverMessageAddress, UserRejectedRequestError } from 'viem';
import {
  erc20ABI,
  erc4626ABI,
  useAccount,
  useBalance,
  useContractRead,
  useContractWrite,
  useDisconnect,
  useFeeData,
  usePrepareContractWrite,
  usePublicClient,
  useSignMessage,
  useWaitForTransaction,
  useWalletClient,
} from 'wagmi';
import MevEthAbi from '../../../MevEthAbi';
import { MevEthRouterAbi } from '../../../MevEthRouterAbi';
import SplitSwapRouterAbi from '../../../SplitSwapRouterAbi';
import BigWhiteButton from '../general/BigWhiteButton';
import {
  AmountContainer,
  AnimatedSpacerContainer,
  CenterFoldContainer,
  FoldInput,
  FoldSymbol,
  ManaTopText,
  MaxButton,
  TopText,
  WethSwitch,
} from '../general/CenterFold';
import MEVETHLogo from '../general/MEVETHLogo';
import {
  WithdrawalOptionCell,
  WithdrawalsOptionContainer,
  WithdrawalsOptionsContainer,
} from '../general/WithdrawalOptionCells';
import { WithdrawalsTicketContainer, WithdrawalTicketCell } from '../general/WithdrawalTicketCells';
import { SectionFAQ } from '../home/AvailableToday';
import ConfirmStakeView from './ConfirmStakeView';
import { PossibleModes } from './StakeTopFold';

type Props = {
  setMode: (mode: PossibleModes) => void;
  mode: PossibleModes;
};
export interface PointsDetails {
  address: string;
  total_points: number;
  has_points: boolean;
  has_votes: boolean;
  has_lp: boolean;
}
export const pointsAtom = atom<PointsDetails>({
  address: '0x0000000000000000000000000000000000000000',
  total_points: 0,
  has_points: false,
  has_votes: false,
  has_lp: false,
});
function EthereumStake({ setMode, mode }: Props) {
  const [signinLoading, setSigninLoading] = useState(false);
  const { data: signMessageData, error, isLoading, signMessage, variables } = useSignMessage();

  const { data: session, status } = useSession();
  const [points, setPoints] = useAtom(pointsAtom);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [loadedPoints, setLoadedPoints] = useState(false);
  const userEmail = session?.user?.email;
  const [verified, setVerified] = useState(false);
  const { address } = useAccount();
  const router = useRouter();
  useEffect(() => {
    const getUserProfile = async () => {
      const res = await fetch('/api/invites/getCurrentProfile');
      const data: UserData = await res.json();
      if (data.message === 'Success' && data.user?.address !== null) {
        router.push('/mana');
      } else {
        const inviteCode = localStorage.getItem('inviteCode');
        if (!inviteCode) {
          router.push('/mana/invites');
        }
      }
    };
    const inviteCode = localStorage.getItem('inviteCode');
    getUserProfile();
  }, []);
  const connectButtonLabel = () => {
    if (!loadedPoints && !loadingPoints) {
      return 'Connect Wallet';
    } else if (loadingPoints) {
      return 'Loading Mana...';
    } else if (loadedPoints && points.total_points === 0) {
      return `You have ${points.total_points} Mana, try another address on your wallet`;
    } else if (loadedPoints && points.total_points > 0) {
      return `✔️ You have ${points.total_points} Mana`;
    }

    return 'Connect Wallet';
  };
  useEffect(() => {
    const postMessage = async () => {
      if (variables?.message && signMessageData && address) {
        const result = await fetch('/api/invites/verifySig', {
          method: 'POST',
          body: JSON.stringify({
            message: variables?.message,
            signature: signMessageData,
            address: address,
          }),
        });
        const answer = await result.json();
        if (answer.valid) {
          setVerified(true);
          router.push('/mana');
        }
        console.log('answer', answer);
      }
    };
    (async () => {
      if (variables?.message && signMessageData) {
        await postMessage();
        const recoveredAddress = await recoverMessageAddress({
          message: variables?.message,
          signature: signMessageData,
        });
        console.log('recoveredAddress', recoveredAddress);
      }
    })();
  }, [signMessageData, variables?.message]);

  useEffect(() => {
    const checkAddress = async () => {
      if (!address) {
        setLoadedPoints(true);

        setPoints({
          address: '0x0000000000000000000000000000000000000000',
          total_points: 0,
          has_points: false,
          has_votes: false,
          has_lp: false,
        });
        return;
      }
      setLoadingPoints(true);
      const { data, error } = await supabase
        .from('total_points_materialized_view_detailed_votes')
        .select()
        .eq('address', address.toLowerCase())
        .single();
      if (!data?.total_points) {
        setLoadedPoints(true);
        setLoadingPoints(false);
        setPoints({
          address:
            address && address !== null ? address : '0x0000000000000000000000000000000000000000',
          total_points: 0,
          has_points: false,
          has_votes: false,
          has_lp: false,
        });
      }
      if (!data) return;
      //@ts-ignore
      const pointsDetails: PointsDetails = data;
      setPoints(pointsDetails);
      setLoadingPoints(false);
      setLoadedPoints(true);
    };
    if (address) {
      checkAddress();
    }
  }, [address]);
  console.log('session', session);
  useEffect(() => {
    const inviteCode = localStorage.getItem('inviteCode');
    if (status === 'authenticated' && inviteCode) {
      fetch('/api/invites/redeem', { method: 'POST', body: JSON.stringify({ inviteCode }) });
    }
  }, [session, status]);

  if (status === 'loading') {
    return <p>Hang on there...</p>;
  }

  return (
    <CenterFoldContainer>
      <ManaTopText>
        1. Follow @mevdotio on Twitter
        <BigWhiteButton
          text={status === 'authenticated' ? '✔️ Following us on X' : 'Follow us on X'}
          disabled={status === 'authenticated'}
          onClick={() => {
            if (status === 'authenticated') return;
            signIn('twitter', {
              redirectTo: `${NEXT_PUBLIC_URL}/api/auth/callback/twitter`,
              callbackUrl: `${NEXT_PUBLIC_URL}/api/auth/callback/twitter`,
              redirect: true,
              redirectUrl: `${NEXT_PUBLIC_URL}/api/auth/callback/twitter`,
            });
          }}
          skipConnect={true}
        ></BigWhiteButton>
      </ManaTopText>

      <ManaTopText>
        2. Check your Mana
        <BigWhiteButton
          disabled={status !== 'authenticated'}
          text={connectButtonLabel()}
          skipConnect={status !== 'authenticated'}
          onClick={() => {}}
        ></BigWhiteButton>
      </ManaTopText>

      <ManaTopText>
        3. Go to Mana
        <BigWhiteButton
          disabled={status !== 'authenticated'}
          text={'Sign Message'}
          skipConnect={true}
          onClick={async () => {
            signMessage({ message: 'Verify me on Mev Protocol' });
          }}
        ></BigWhiteButton>
      </ManaTopText>
    </CenterFoldContainer>
  );
}

function UseMevETH() {
  const [selectedProtocol, setSelectedProtocol] = useState('');

  return (
    <CenterFoldContainer>
      <TopText>You can use your mevETH to earn rewards on other protocols:</TopText>
    </CenterFoldContainer>
  );
}

const PartnerDetails = styled.div`
  margin-left: 10px;
  margin-top: 10px;
  color: white;
`;
const Partner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  margin-top: 10px;
  align-items: left;
`;

const LogosRow = styled.div`
  justify-content: left;
  align-items: left;
  gap: 24px;
`;

const SVGImage = styled.img<{ isActive: boolean }>`
  height: 40px;
  width: 40px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
  &:hover {
    ${({ isActive }) =>
      !isActive &&
      'opacity: ${({ isActive }) => (isActive ? 1 : 0.8)}; cursor: pointer; transform: scale(1.05)'}
  }

  transition: all 0.2s ease-in-out;
`;

const ExplainerText = styled(motion.div)`
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  height: 48px;
  margin-top: 16px;

  @media ${device.mobileS} {
    font-size: 14px;
  }
`;
export default function StakeCenterFold({ setMode, mode }: Props) {
  return <EthereumStake setMode={setMode} mode={mode} />;
}
